import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { EmptyStateProps } from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { DataTableEndlessPaginatedPageScroll } from '@/components/data-table/page-scroll';
import { Button } from '@/components/ui/button';

import { TIME_OFFS_TABLE_ID } from '../../constants/time-offs-filter-keys';
import { TimeOffRequest } from '../../types/time-off-request';
import { TimeOffTimeCategory } from '../../types/time-off-time-category';

interface TimeOffsTableProps {
  table: Table<TimeOffRequest>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  onResizeEnd: () => void;
  hasActiveFilters: boolean;
  onRequestTimeOff?: () => void;
  /** Navigates to a request's details screen when its row is clicked. */
  onRowClick?: (request: TimeOffRequest) => void;
  getRowHref?: (request: TimeOffRequest) => string | undefined;
  /** Top offset (px) for the sticky column header — pins below the page chrome. */
  stickyHeaderTopPx?: number;
  /**
   * Table id for column sizing/visibility persistence. Defaults to the personal
   * page's id; the team-member tab passes its own so the two don't collide.
   */
  tableId?: string;
}

export const TimeOffsTable = ({
  table,
  isLoading,
  hasMore,
  onLoadMore,
  error,
  onResizeEnd,
  hasActiveFilters,
  onRequestTimeOff,
  onRowClick,
  getRowHref,
  stickyHeaderTopPx,
  tableId = TIME_OFFS_TABLE_ID,
}: TimeOffsTableProps) => {
  const { t } = useTranslation(['authenticated.timeOff', 'common']);

  const renderEmptyState = ({ colSpan }: EmptyStateProps) => {
    if (hasActiveFilters) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return (
      <tr>
        <td colSpan={colSpan} className="w-full px-0 py-4">
          <StickyTableStateContent className="items-start gap-3 px-8 text-left">
            <div className="flex flex-col gap-0.5">
              <div className="text-sm font-semibold text-foreground">{t('table.emptyTitle')}</div>
              <div className="text-xs text-muted-foreground">{t('table.emptyDescription')}</div>
            </div>
            {onRequestTimeOff && (
              <Button variant="default" size="sm" className="h-9 gap-2" onClick={onRequestTimeOff}>
                <CirclePlus className="h-4 w-4" />
                {t('header.requestTimeOff')}
              </Button>
            )}
          </StickyTableStateContent>
        </td>
      </tr>
    );
  };

  return (
    <DataTableEndlessPaginatedPageScroll
      table={table}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      error={error}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
      onRowClick={onRowClick}
      getRowHref={getRowHref}
      stickyHeaderTopPx={stickyHeaderTopPx}
      renderEmptyState={renderEmptyState}
      getRowClassName={row =>
        row.timeCategory === TimeOffTimeCategory.Past ? 'bg-muted/30' : undefined
      }
    />
  );
};
